// @flow
import React, {useEffect, useMemo} from 'react';
import PropTypes from 'prop-types';
import {
  Button,
  ButtonGroup,
  Grid,
  TextField,
  Typography,
  makeStyles,
} from '@material-ui/core';
import {
  UseFormRegister,
  FieldValues,
  useWatch,
  UseFormSetValue,
  Control,
} from 'react-hook-form';

import OptionsForm from '../OptionsForm/OptionsForm';

import {Add, Remove} from '@material-ui/icons';
import {colors} from '../../../../../assets/styles/colors';
import {useSafePrevious} from '../../../../../utils/react';

import type {CheckoutInput} from '../../../../../entities';
import type {Theme} from '@material-ui/core';
type Props = {
  checkoutInput?: CheckoutInput,
  control: Control,
  register: UseFormRegister<FieldValues>,
  setValue: UseFormSetValue<FieldValues>,
  errors: {[key: string]: any},
  options: string[],
  setOptions: (string[] | ((string[]) => string[])) => void,
};

const useStyles = makeStyles((theme: Theme) => ({
  wrapper: {
    padding: theme.spacing(1.5),
    boxShadow:
      '0px 0px 1px rgba(40, 41, 61, 0.08), 0px 0.5px 2px rgba(96, 97, 112, 0.16)',
    backgroundColor: 'white',
    borderRadius: '12px',
    border: `1px solid ${colors.grey300}`,
    display: 'flex',
    flexDirection: 'column',
    gap: '16px',
  },
  inputWrap: {
    display: 'flex',
    flexDirection: 'column',
    gap: '4px',
  },
  text: {
    fontWeight: 500,
    fontSize: 14,
    color: colors.grey900,
  },
  greyText: {
    fontWeight: 400,
    fontSize: 14,
    color: colors.grey700,
  },
  wrap: {
    width: '100%',
  },
  rowOptional: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
  },
  textInput: {
    textAlign: 'center',
  },
  optionLabel: {
    color: '#500BDB',
    fontWeight: 500,
    fontSize: 12,
  },
  optionChip: {
    background: colors.lightPurple,
  },
  error: {color: colors.error, fontSize: 14},
}));

export default function MultiOptionForm({
  checkoutInput,
  control,
  setValue,
  register,
  errors,
  options,
  setOptions,
}: Props) {
  const classes = useStyles();
  const prevOptions = useSafePrevious<string[]>(options);

  const maxOptionsWatched = useWatch({
    control,
    name: 'maxOptions',
  });

  const minOptionsWatched = useWatch({
    control,
    name: 'minOptions',
  });

  const handleMinOptions = (value: number) => {
    setValue('minOptions', Number(minOptionsWatched) + value);
  };

  const handleMaxOptions = (value: number) => {
    setValue('maxOptions', Number(maxOptionsWatched) + value);
  };

  const isDisabledAddMax = Number(maxOptionsWatched) >= options.length;
  const isDisabledAddMin = Number(minOptionsWatched) >= options.length;

  const minOptionsText = useMemo(() => {
    const minOptions = Number(minOptionsWatched)?.toFixed(0);
    return `El mínimo
    ${Number(minOptions) > 1 ? `son ${minOptions} opciones.` : '1 opción'}`;
  }, [minOptionsWatched]);

  const maxOptionsText = useMemo(() => {
    const maxOptions = Number(maxOptionsWatched)?.toFixed(0);
    return `El máximo
    ${Number(maxOptions) > 1 ? `son ${maxOptions} opciones.` : '1 opción'}`;
  }, [maxOptionsWatched]);

  const defaultMinOptions =
    prevOptions?.length === 1 && options.length === 2
      ? options.length
      : checkoutInput?.schema?.min_selected_options;

  const defaultMaxOptions =
    prevOptions?.length === 1 && options.length === 2
      ? options.length
      : checkoutInput?.schema?.max_selected_options;

  useEffect(() => {
    if (checkoutInput) {
      const schema = checkoutInput.schema;
      setValue('minOptions', schema?.min_selected_options);
      setValue('maxOptions', schema?.max_selected_options);
    }
  }, [checkoutInput, setValue]);

  useEffect(() => {
    if (prevOptions?.length === 1 && options.length === 2) {
      setValue('minOptions', 2);
      setValue('maxOptions', 2);
    }
  }, [options.length, prevOptions, setValue]);

  return (
    <>
      <Grid item xs={12}>
        <Grid container spacing={1}>
          <Grid item xs={12}>
            <Typography className={classes.text}>Opciones</Typography>
          </Grid>
          <Grid item xs={12}>
            <OptionsForm options={options} setOptions={setOptions} />
          </Grid>
        </Grid>
      </Grid>

      {options.length > 1 && (
        <Grid item xs={12}>
          <Grid container spacing={2}>
            <Grid item xs={12} md={6}>
              <Grid container spacing={1}>
                <Grid item xs={12}>
                  <Typography className={classes.text}>
                    ¿Cuántas opciones debe elegir el cliente?
                  </Typography>
                  <Typography className={classes.greyText}>
                    {minOptionsText}
                  </Typography>
                </Grid>
                <Grid item xs={12}>
                  <ButtonGroup size="small" fullWidth>
                    <Button
                      size="small"
                      className={classes.actionButton}
                      onClick={() => handleMinOptions(-1)}
                      disabled={Number(minOptionsWatched) <= 2}
                    >
                      <Remove fontSize="small" />
                    </Button>
                    <TextField
                      name="minOptions"
                      defaultValue={defaultMinOptions}
                      inputProps={{className: classes.textInput}}
                      inputRef={register({
                        required: 'Cantidad incorrecta',
                        pattern: {
                          value: /^[0-9]+([.][0-9]+)?$/i,
                          message: 'Cantidad incorrecta',
                        },
                        min: {
                          value: 1,
                          message: 'Cantidad incorrecta',
                        },
                        max: {
                          value: Number(maxOptionsWatched) + 1,
                          message: 'Cantidad incorrecta',
                        },
                      })}
                      type="number"
                      error={!!errors.minOptions}
                      step="1"
                    />
                    <Button
                      size="small"
                      className={classes.actionButton}
                      onClick={() => handleMinOptions(1)}
                      disabled={isDisabledAddMin}
                    >
                      <Add fontSize="small" />
                    </Button>
                  </ButtonGroup>
                </Grid>
                {!!errors.minOptions && (
                  <Grid item xs={12}>
                    <Typography className={classes.error}>
                      {errors.minOptions.message}
                    </Typography>
                  </Grid>
                )}
              </Grid>
            </Grid>
            <Grid item xs={12} md={6}>
              <Grid container spacing={1}>
                <Grid item xs={12}>
                  <Typography className={classes.text}>
                    ¿Cuántas opciones como máximo debe elegir el cliente?
                  </Typography>
                  <Typography className={classes.greyText}>
                    {maxOptionsText}
                  </Typography>
                </Grid>
                <Grid item xs={12}>
                  <ButtonGroup size="small" fullWidth>
                    <Button
                      size="small"
                      className={classes.actionButton}
                      onClick={() => handleMaxOptions(-1)}
                      disabled={Number(maxOptionsWatched) <= 2}
                    >
                      <Remove fontSize="small" />
                    </Button>
                    <TextField
                      name="maxOptions"
                      inputProps={{className: classes.textInput}}
                      defaultValue={defaultMaxOptions}
                      inputRef={register({
                        required: 'Cantidad incorrecta',
                        pattern: {
                          value: /^[0-9]+([.][0-9]+)?$/i,
                          message: 'Cantidad incorrecta',
                        },
                        min: {
                          value: Number(minOptionsWatched),
                          message: 'Cantidad incorrecta',
                        },
                        max: {
                          value: options.length,
                          message: 'Cantidad incorrecta',
                        },
                      })}
                      type="number"
                      error={!!errors.maxOptions}
                      step="1"
                    />
                    <Button
                      size="small"
                      className={classes.actionButton}
                      onClick={() => handleMaxOptions(1)}
                      disabled={isDisabledAddMax}
                    >
                      <Add fontSize="small" />
                    </Button>
                  </ButtonGroup>
                </Grid>
                {!!errors.maxOptions && (
                  <Grid item xs={12}>
                    <Typography className={classes.error}>
                      {errors.maxOptions.message}
                    </Typography>
                  </Grid>
                )}
              </Grid>
            </Grid>
          </Grid>
        </Grid>
      )}
    </>
  );
}

MultiOptionForm.propTypes = {
  options: PropTypes.array.isRequired,
  setOptions: PropTypes.func.isRequired,
  minOptions: PropTypes.number.isRequired,
  setMinOptions: PropTypes.func.isRequired,
  maxOptions: PropTypes.func.isRequired,
  setMaxOptions: PropTypes.func.isRequired,
};
